import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiLayers,
  FiEdit2,
  FiCalendar,
  FiCheck,
  FiSave,
  FiX,
  FiAlertTriangle,
  FiTrendingUp,
  FiArrowLeft,
  FiRefreshCw,
  FiInfo,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import portalService from "../../../services/portalService";

function ProgressBar({ percent, color = "#003b67", height = 8 }) {
  const safePercent = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div
      style={{
        width: "100%",
        height,
        background: "#e2e8f0",
        borderRadius: 99,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          width: `${safePercent}%`,
          height: "100%",
          background: color,
          borderRadius: 99,
          transition: "width 0.4s ease",
        }}
      />
    </div>
  );
}

export default function TeacherSyllabusTracker() {
  const { academicYear, academicYears } = useApp();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syllabusList, setSyllabusList] = useState([]);
  const [selectedAY, setSelectedAY] = useState(academicYear?._id || "");

  // Unit edit modal state
  const [editingSubject, setEditingSubject] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [savingUnit, setSavingUnit] = useState(false);

  // Unit edit form fields
  const [unitStatus, setUnitStatus] = useState("Not Started");
  const [unitActualHours, setUnitActualHours] = useState(0);
  const [unitStartDate, setUnitStartDate] = useState("");
  const [unitCompletionDate, setUnitCompletionDate] = useState("");
  const [unitTopicsCovered, setUnitTopicsCovered] = useState([]);
  const [unitRemarks, setUnitRemarks] = useState("");

  useEffect(() => {
    if (academicYear?._id && !selectedAY) {
      setSelectedAY(academicYear._id);
    }
  }, [academicYear, selectedAY]);

  const loadSyllabus = useCallback(async () => {
    setLoading(true);
    try {
      const ayId = selectedAY || academicYear?._id || "";
      const res = await portalService.getTeacherSyllabusList(ayId ? { academicYear: ayId } : {});
      const list = res?.data || (Array.isArray(res) ? res : []);
      setSyllabusList(Array.isArray(list) ? list : []);
      if (res?.academicYear && !selectedAY) {
        setSelectedAY(res.academicYear);
      }
    } catch (err) {
      console.error("Failed to load teacher syllabus list:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to load syllabus tracking");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedAY, academicYear]);

  useEffect(() => {
    loadSyllabus();
  }, [loadSyllabus]);

  const handleOpenUnitModal = (subjectItem, unit) => {
    setEditingSubject(subjectItem);
    setEditingUnit(unit);
    setUnitStatus(unit.status || "Not Started");
    setUnitActualHours(unit.actualHours || 0);
    setUnitStartDate(unit.startDate || "");
    setUnitCompletionDate(unit.completionDate || (unit.status === "Completed" ? new Date().toISOString().slice(0, 10) : ""));
    setUnitTopicsCovered(unit.topicsCovered || []);
    setUnitRemarks(unit.remarks || "");
  };

  const handleStatusChange = (newStatus) => {
    setUnitStatus(newStatus);
    if (newStatus === "Completed" && !unitCompletionDate) {
      setUnitCompletionDate(new Date().toISOString().slice(0, 10));
    }
    if (newStatus === "In Progress" && !unitStartDate) {
      setUnitStartDate(new Date().toISOString().slice(0, 10));
    }
  };

  const handleToggleTopic = (topic) => {
    setUnitTopicsCovered((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic],
    );
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    if (!editingSubject || !editingUnit) return;

    setSavingUnit(true);
    try {
      const ayId = selectedAY || academicYear?._id;
      await portalService.updateTeacherUnitProgress(
        editingSubject.subject._id,
        editingUnit.unitNo,
        {
          academicYear: ayId,
          status: unitStatus,
          actualHours: Number(unitActualHours) || 0,
          startDate: unitStartDate || null,
          completionDate: unitCompletionDate || null,
          topicsCovered: unitTopicsCovered,
          remarks: unitRemarks,
        },
      );

      toast.success(`Unit ${editingUnit.unitNo} updated successfully!`);
      setEditingUnit(null);
      setEditingSubject(null);
      loadSyllabus();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update unit progress");
    } finally {
      setSavingUnit(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FiLayers color="#7c3aed" /> My Subject Syllabus Tracker
          </h1>
          <p className="page-subtitle">
            Record unit completion, track actual lecture hours against fixed curriculum hours, and log teaching notes.
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {/* Academic Year Selector */}
          <select
            className="form-control form-control-sm"
            style={{ width: "auto", minWidth: 140, fontWeight: 600 }}
            value={selectedAY}
            onChange={(e) => setSelectedAY(e.target.value)}
          >
            {academicYears?.length > 0 ? (
              academicYears.map((ay) => (
                <option key={ay._id} value={ay._id}>
                  {ay.label} {ay.isActive ? "(Active)" : ""}
                </option>
              ))
            ) : academicYear ? (
              <option value={academicYear._id}>
                {academicYear.label} (Active)
              </option>
            ) : (
              <option value="">Current Academic Year</option>
            )}
          </select>


          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              setRefreshing(true);
              loadSyllabus();
            }}
            disabled={refreshing}
            title="Refresh"
          >
            <FiRefreshCw size={13} className={refreshing ? "spin" : ""} />
          </button>
        </div>
      </div>

      {/* ── Loading / Empty ── */}
      {loading ? (
        <div className="loading-center">
          <div className="spinner" />
          Loading your assigned syllabus…
        </div>
      ) : syllabusList.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
          <FiBookOpen size={48} style={{ opacity: 0.2, margin: "0 auto 1rem", display: "block" }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>No Subjects Assigned</h3>
          <p style={{ color: "var(--text-muted)", maxWidth: 450, margin: "0 auto" }}>
            You do not have any subjects assigned for this academic year yet. Contact the college administrator if you teach classes this semester.
          </p>
        </div>
      ) : (
        /* ── Subject Syllabus Cards ── */
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          {syllabusList.map((item) => {
            const { subject, progress } = item;
            const units = progress?.units || [];
            const pct = progress?.completionPercentage || 0;
            const doneCount = progress?.completedUnitsCount || 0;
            const totalUnits = progress?.totalUnitsCount || 0;
            const plannedHrs = progress?.totalPlannedHours || 0;
            const actualHrs = progress?.totalActualHours || 0;
            const variance = actualHrs - plannedHrs;

            return (
              <div key={subject._id} className="card" style={{ padding: "1.25rem" }}>
                {/* Header Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "0.75rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                      <span className="badge badge-blue">
                        {subject.course?.shortName || subject.course?.courseName || "Course"} · Year {subject.yearNumber}
                      </span>
                      <span style={{ fontSize: 11, background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 99, fontWeight: 700 }}>
                        {subject.type}
                      </span>
                      {subject.code && <span className="badge badge-gray">{subject.code}</span>}
                    </div>
                    <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                      {subject.name}
                    </h2>
                  </div>

                  {/* Summary Metric Pills */}
                  <div style={{ display: "flex", gap: "0.65rem", alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ background: "#f8fafc", border: "1px solid var(--border)", padding: "0.4rem 0.75rem", borderRadius: 8, textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Units Done</div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {doneCount} / {totalUnits}
                      </div>
                    </div>

                    <div style={{ background: "#f8fafc", border: "1px solid var(--border)", padding: "0.4rem 0.75rem", borderRadius: 8, textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Hours (Act / Plan)</div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>
                        {actualHrs}h / {plannedHrs}h
                      </div>
                    </div>

                    <div
                      style={{
                        background: pct === 100 ? "#dcfce7" : "#e8f1f8",
                        color: pct === 100 ? "#15803d" : "#002747",
                        border: `1px solid ${pct === 100 ? "#86efac" : "#b6d5ec"}`,
                        padding: "0.4rem 0.85rem",
                        borderRadius: 8,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600 }}>Completion</div>
                      <div style={{ fontSize: 16, fontWeight: 900 }}>{pct}%</div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: "1rem" }}>
                  <ProgressBar percent={pct} color={pct === 100 ? "#16a34a" : "#7c3aed"} height={8} />
                </div>

                {/* Unit Cards Grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0.75rem" }}>
                  {units.length === 0 ? (
                    <div style={{ gridColumn: "1 / -1", color: "var(--text-muted)", fontSize: 13, fontStyle: "italic", padding: "1rem 0" }}>
                      No units defined in the syllabus catalog for this subject yet.
                    </div>
                  ) : (
                    units.map((unit) => {
                      const isCompleted = unit.status === "Completed";
                      const isInProgress = unit.status === "In Progress";
                      const unitDiff = (Number(unit.actualHours) || 0) - (Number(unit.plannedHours) || 0);

                      return (
                        <div
                          key={unit.unitNo}
                          style={{
                            border: `1px solid ${isCompleted ? "#86efac" : isInProgress ? "#93c5fd" : "var(--border)"}`,
                            background: isCompleted ? "#f0fdf4" : isInProgress ? "#e8f1f8" : "#fff",
                            borderRadius: 10,
                            padding: "0.85rem",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            boxShadow: isCompleted || isInProgress ? "0 2px 6px rgba(0,0,0,0.03)" : "none",
                          }}
                        >
                          <div>
                            {/* Unit Top */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: isCompleted ? "#16a34a" : isInProgress ? "#003b67" : "var(--text-muted)",
                                }}
                              >
                                UNIT {unit.unitNo}
                              </span>
                              <span
                                style={{
                                  background: isCompleted ? "#dcfce7" : isInProgress ? "#e8f1f8" : "#f1f5f9",
                                  color: isCompleted ? "#15803d" : isInProgress ? "#002747" : "#475569",
                                  padding: "1px 7px",
                                  borderRadius: 99,
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}
                              >
                                {unit.status}
                              </span>
                            </div>

                            {/* Title */}
                            <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>
                              {unit.title}
                            </div>

                            {/* Hours breakdown */}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
                              <span>
                                Planned: <strong>{unit.plannedHours || 0} hrs</strong>
                              </span>
                              <span>
                                Actual: <strong style={{ color: isCompleted && unitDiff > 0 ? "#dc2626" : "inherit" }}>{unit.actualHours || 0} hrs</strong>
                                {isCompleted && unitDiff !== 0 && (
                                  <span style={{ marginLeft: 3, fontWeight: 700, color: unitDiff > 0 ? "#dc2626" : "#16a34a" }}>
                                    ({unitDiff > 0 ? `+${unitDiff}h` : `${unitDiff}h`})
                                  </span>
                                )}
                              </span>
                            </div>

                            {/* Dates & topics count */}
                            {unit.completionDate && (
                              <div style={{ fontSize: 11.5, color: "#15803d", display: "flex", alignItems: "center", gap: "0.25rem", marginBottom: 4 }}>
                                <FiCheck size={11} /> Completed on {unit.completionDate}
                              </div>
                            )}

                            {unit.topicsTotal && unit.topicsTotal.length > 0 && (
                              <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginBottom: 4 }}>
                                Topics covered: {unit.topicsCovered?.length || 0} of {unit.topicsTotal.length}
                              </div>
                            )}
                          </div>

                          {/* Action button */}
                          <button
                            className={`btn btn-sm ${isCompleted ? "btn-outline" : "btn-primary"}`}
                            onClick={() => handleOpenUnitModal(item, unit)}
                            style={{
                              width: "100%",
                              justifyContent: "center",
                              marginTop: "0.75rem",
                              fontSize: 12,
                            }}
                          >
                            <FiEdit2 size={12} /> {isCompleted ? "Edit Unit Log" : "Update Progress"}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════════════
         MODAL: Update Unit Progress Dialog
         ═════════════════════════════════════════════════════════════════════════ */}
      {editingUnit && editingSubject && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
          onClick={() => {
            if (!savingUnit) {
              setEditingUnit(null);
              setEditingSubject(null);
            }
          }}
        >
          <div
            className="modal-container"
            style={{
              background: "#fff",
              borderRadius: 14,
              maxWidth: 580,
              width: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.25rem 1.5rem",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase" }}>
                  Unit {editingUnit.unitNo} — {editingSubject.subject.name}
                </div>
                <h2 style={{ fontSize: 16, fontWeight: 800, margin: "2px 0 0" }}>
                  {editingUnit.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!savingUnit) {
                    setEditingUnit(null);
                    setEditingSubject(null);
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "var(--text-muted)",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveUnit} style={{ padding: "1.25rem 1.5rem" }}>
              {/* Unit Status Selector */}
              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label className="form-label" style={{ fontWeight: 700 }}>
                  Completion Status *
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem" }}>
                  {["Not Started", "In Progress", "Completed"].map((st) => {
                    const isSel = unitStatus === st;
                    return (
                      <button
                        key={st}
                        type="button"
                        onClick={() => handleStatusChange(st)}
                        style={{
                          padding: "0.6rem 0.5rem",
                          borderRadius: 8,
                          border: `2px solid ${
                            isSel
                              ? st === "Completed"
                                ? "#16a34a"
                                : st === "In Progress"
                                ? "#003b67"
                                : "#475569"
                              : "var(--border)"
                          }`,
                          background: isSel
                            ? st === "Completed"
                              ? "#f0fdf4"
                              : st === "In Progress"
                              ? "#e8f1f8"
                              : "#f8fafc"
                            : "#fff",
                          fontWeight: isSel ? 800 : 500,
                          color: isSel
                            ? st === "Completed"
                              ? "#15803d"
                              : st === "In Progress"
                              ? "#002747"
                              : "#1e293b"
                            : "var(--text)",
                          fontSize: 12.5,
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        {st === "Completed" && "✓ "}
                        {st}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Hours Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                  background: "#f8fafc",
                  padding: "0.85rem",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                }}
              >
                <div>
                  <label className="form-label" style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                    Fixed / Planned Hours (Catalog)
                  </label>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {editingUnit.plannedHours || 0} Hours
                  </div>
                </div>

                <div>
                  <label className="form-label" style={{ fontSize: 11.5, fontWeight: 700 }}>
                    Actual Hours Taken *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="form-control"
                    value={unitActualHours}
                    onChange={(e) => setUnitActualHours(e.target.value)}
                    required
                    placeholder="e.g. 12"
                    style={{ fontWeight: 700 }}
                  />
                  {Number(unitActualHours) > Number(editingUnit.plannedHours) && (
                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3 }}>
                      ⚠ +{(Number(unitActualHours) - Number(editingUnit.plannedHours)).toFixed(1)} hrs over fixed limit
                    </div>
                  )}
                </div>
              </div>

              {/* Dates Row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Teaching Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={unitStartDate}
                    onChange={(e) => setUnitStartDate(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Completion Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={unitCompletionDate}
                    onChange={(e) => setUnitCompletionDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Topics Checklist */}
              {editingUnit.topicsTotal && editingUnit.topicsTotal.length > 0 && (
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label className="form-label" style={{ fontWeight: 700, display: "flex", justifyContent: "space-between" }}>
                    <span>Topics Checklist</span>
                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>
                      {unitTopicsCovered.length} of {editingUnit.topicsTotal.length} covered
                    </span>
                  </label>
                  <div
                    style={{
                      maxHeight: 140,
                      overflowY: "auto",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "0.5rem 0.75rem",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.4rem",
                    }}
                  >
                    {editingUnit.topicsTotal.map((topic, ti) => {
                      const isCovered = unitTopicsCovered.includes(topic);
                      return (
                        <label
                          key={ti}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            fontSize: 12.5,
                            cursor: "pointer",
                            padding: "3px 0",
                            userSelect: "none",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isCovered}
                            onChange={() => handleToggleTopic(topic)}
                            style={{ cursor: "pointer" }}
                          />
                          <span
                            className="rich-topic-display"
                            style={{
                              textDecoration: isCovered ? "none" : "none",
                              color: isCovered ? "var(--text)" : "var(--text-muted)",
                              display: "inline-block",
                            }}
                            dangerouslySetInnerHTML={{ __html: topic }}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Remarks / Deviation Notes */}
              <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                <label className="form-label">Teaching Notes / Remarks</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="e.g. Conducted additional revision lecture, demonstrated practical instruments..."
                  value={unitRemarks}
                  onChange={(e) => setUnitRemarks(e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* Submit Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setEditingUnit(null);
                    setEditingSubject(null);
                  }}
                  disabled={savingUnit}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingUnit}>
                  {savingUnit ? (
                    <>
                      <div className="spinner-sm" /> Saving…
                    </>
                  ) : (
                    <>
                      <FiSave size={13} /> Save Unit Progress
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
